import React, { useEffect, useRef, useState } from 'react';
import { navLinks } from '../../data/navigation';
import PeakLogo from '../ui/PeakLogo';
import Button from '../ui/Button';
import Container from './Container';
import { colors, font } from '../../lib/tokens';

// True once the page has scrolled past the top, which switches the navbar
// from flush-on-hero to its frosted, hairlined state (styles in index.css).
function useScrolled(threshold = 8) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return scrolled;
}

// The nav section currently on screen. A section is "current" while it
// crosses a thin band 40% down the viewport; between nav sections (e.g. the
// testimonial) nothing is highlighted.
function useActiveSection(ids: readonly string[]) {
  const [active, setActive] = useState<string | null>(null);
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return;
    const visible = new Set<string>();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => (e.isIntersecting ? visible.add(e.target.id) : visible.delete(e.target.id)));
        setActive(ids.find((id) => visible.has(id)) ?? null);
      },
      { rootMargin: '-40% 0px -59% 0px' }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, [ids]);
  return active;
}

const SECTION_IDS = navLinks.map((l) => l.sectionId);

export default function Navbar() {
  const scrolled = useScrolled();
  const active = useActiveSection(SECTION_IDS);
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // A closed menu is collapsed to zero height but still in the DOM; `inert`
  // keeps its links out of the tab order and the accessibility tree. (Set via
  // the DOM because React 18 doesn't type the attribute.)
  useEffect(() => {
    menuRef.current?.toggleAttribute('inert', !menuOpen);
  }, [menuOpen]);

  // Close the phone menu on Escape, on a tap outside the bar, and if the
  // viewport grows past the breakpoint where the inline links take over.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    const onPointer = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const mq = window.matchMedia('(min-width: 768px)');
    const onMq = () => mq.matches && setMenuOpen(false);
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    mq.addEventListener('change', onMq);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
      mq.removeEventListener('change', onMq);
    };
  }, [menuOpen]);

  const scrollTo = (id: string) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div ref={rootRef} className="kc-nav" data-scrolled={scrolled} data-menu-open={menuOpen}>
      <Container
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 72,
        }}
      >
        <a href="/" aria-label="KatlaCode home" className="kc-logo-link" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <PeakLogo color={colors.ink} accent={colors.blue} size={30} />
        </a>

        {/* Desktop nav — hidden on narrow viewports */}
        <nav aria-label="Sections" style={{ gap: 32, fontSize: 14, fontFamily: font.sans }} className="hidden md:flex">
          {navLinks.map((link) => (
            <button
              key={link.sectionId}
              onClick={() => scrollTo(link.sectionId)}
              className="kc-nav-link"
              aria-current={active === link.sectionId ? 'true' : undefined}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: 14,
                padding: 0,
                fontFamily: 'inherit',
              }}
            >
              {link.label}
            </button>
          ))}
        </nav>

        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {/* CTA — visible on all viewports */}
          <Button variant="primary" href="mailto:info@katlacode.ai">Contact us</Button>
          <button
            type="button"
            className="kc-menu-toggle"
            aria-expanded={menuOpen}
            aria-controls="kc-mobile-menu"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span className="kc-menu-toggle-icon" aria-hidden>
              <span className="kc-menu-toggle-bar" />
              <span className="kc-menu-toggle-bar" />
            </span>
          </button>
        </div>
      </Container>

      {/* Phone menu — drops open under the bar, overlaying the page */}
      <div id="kc-mobile-menu" className="kc-menu" data-open={menuOpen}>
        <div ref={menuRef} className="kc-menu-inner">
          <Container style={{ paddingBottom: 16 }}>
            <nav aria-label="Sections">
              {navLinks.map((link, i) => (
                <button
                  key={link.sectionId}
                  className="kc-menu-link"
                  style={{ '--i': i } as React.CSSProperties}
                  aria-current={active === link.sectionId ? 'true' : undefined}
                  onClick={() => scrollTo(link.sectionId)}
                >
                  {link.label}
                </button>
              ))}
            </nav>
          </Container>
        </div>
      </div>
    </div>
  );
}
